import React, { useState, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import OrgTreeSelect from '../../components/OrgSelect/OrgTreeSelect';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Paper,
  Divider,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  AccountTree as AccountTreeIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  Badge as BadgeIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Work as WorkIcon
} from '@mui/icons-material';
import '../../css/users.css';

function UserCreate() {
  const [form, setForm] = useState({
    userid: '',
    name: '',
    password: '',
    role: 'user',
    phone: '',
    email: '',
    dept_code: '',
    dept_name: '',
    parent_dept_code: '',
    position: '',
    maildb: false,
  });

  const [showOrgPopup, setShowOrgPopup] = useState(false);
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);

  const handleConfirmOrgSelection = (targets) => {
    if (targets.length > 0) {
      const selected = targets[0];
      setForm({
        ...form,
        dept_code: selected.target_id,
        dept_name: selected.target_name,
        parent_dept_code: selected.parent_dept_code || '',
      });
    }
    setShowOrgPopup(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async () => {
    try {
      if (!form.userid || !form.password) {
        return alert('아이디와 비밀번호를 입력하세요');
      }

      await axios.post('/api/auth/register', form, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert('사용자가 등록되었습니다');
      navigate('/users');
    } catch (err) {
      console.error('사용자 등록 실패:', err);
      alert(err.response?.data?.message || '등록 중 오류 발생');
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid #e0e4e8', bgcolor: '#fff' }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 4,
          pb: 2,
          borderBottom: '2px solid #2e7d32'
        }}>
          <PersonAddIcon sx={{ color: '#2e7d32', fontSize: '2rem' }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
              사용자 신규 등록
            </Typography>
            <Typography variant="body2" sx={{ color: '#6c757d' }}>
              시스템에 새로운 구성원을 등록합니다.
            </Typography>
          </Box>
        </Box>

        <Stack spacing={3}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#495057' }}>
                아이디 <Box component="span" sx={{ color: 'error.main' }}>*</Box>
              </Typography>
              <TextField
                fullWidth
                size="small"
                name="userid"
                value={form.userid}
                onChange={handleChange}
                placeholder="아이디 입력"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon fontSize="small" sx={{ color: '#adb5bd' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#495057' }}>
                이름 <Box component="span" sx={{ color: 'error.main' }}>*</Box>
              </Typography>
              <TextField
                fullWidth
                size="small"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="이름 입력"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeIcon fontSize="small" sx={{ color: '#adb5bd' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#495057' }}>
                비밀번호 <Box component="span" sx={{ color: 'error.main' }}>*</Box>
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                placeholder="비밀번호 입력"
                autoComplete="new-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon fontSize="small" sx={{ color: '#adb5bd' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#495057' }}>
                권한
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  name="role"
                  value={form.role}
                  onChange={handleChange}
                >
                  <MenuItem value="user">일반 사용자 (user)</MenuItem>
                  <MenuItem value="admin">관리자 (admin)</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Divider sx={{ my: 1 }} />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#495057' }}>
              부서 선택
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                value={form.dept_name || ''}
                placeholder="부서를 선택하세요"
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccountTreeIcon fontSize="small" sx={{ color: '#adb5bd' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                onClick={() => setShowOrgPopup(true)}
                startIcon={<SearchIcon />}
                sx={{ whiteSpace: 'nowrap' }}
              >
                선택
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setForm({ ...form, dept_code: '', dept_name: '', parent_dept_code: '' })}
                startIcon={<ClearIcon />}
                sx={{ whiteSpace: 'nowrap' }}
              >
                삭제
              </Button>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#495057' }}>
                직책
              </Typography>
              <TextField
                fullWidth
                size="small"
                name="position"
                value={form.position}
                onChange={handleChange}
                placeholder="직책 입력"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WorkIcon fontSize="small" sx={{ color: '#adb5bd' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#495057' }}>
                연락처
              </Typography>
              <TextField
                fullWidth
                size="small"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="연락처 입력"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon fontSize="small" sx={{ color: '#adb5bd' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.maildb}
                  onChange={(e) => setForm({ ...form, maildb: e.target.checked, email: e.target.checked ? '' : form.email })}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>이메일 자동 생성</Typography>
                  {form.maildb && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      체크 시 시스템 계정 기반으로 메일 계정이 자동 생성됩니다.
                    </Typography>
                  )}
                </Box>
              }
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#495057' }}>
              이메일 주소
            </Typography>
            <TextField
              fullWidth
              size="small"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="이메일 입력"
              disabled={form.maildb}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon fontSize="small" sx={{ color: '#adb5bd' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" spacing={1.5} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              color="success"
              onClick={handleSubmit}
              sx={{ px: 8, py: 1, fontWeight: 'bold', borderRadius: 2 }}
            >
              사용자 등록
            </Button>
            <Button
              variant="outlined"
              size="large"
              color="secondary"
              onClick={() => navigate('/users')}
              sx={{ px: 4, py: 1, borderRadius: 2 }}
            >
              취소
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {showOrgPopup && (
        <OrgTreeSelect
          selectionMode="single"
          targetType="dept"
          onConfirm={handleConfirmOrgSelection}
          onCancel={() => setShowOrgPopup(false)}
        />
      )}
    </Box>
  );
}

export default UserCreate;